import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { actions as warningActions } from 'state/warnings';
import { actions as errorActions } from 'state/errors';

import {
  actions as orderActions,
  selectors as orderSelectors,
} from 'state/order';
import {
  actions as shipmentsActions,
  selectors as shipmentSelectors,
} from 'state/shipments';

import { formatters as addressFormatters } from 'utils/addresses';
import { isEmpty } from 'utils/types';
import keyGenerator from 'utils/keys';

import {
  constants as dateAndTimeConstants,
  formatters as dateAndTimeFormatters,
  validators as dateAndTimeValidators,
} from 'utils/dateAndTimes';
import moment from 'moment-timezone';

import shipmentsSelectors from 'state/shipments/selectors';
import actions from 'state/mrDistribution/shipments/actionCreators';

import types from 'state/mrDistribution/shipments/actionTypes';
import utils from 'state/mrDistribution/shipments/utils';
import {
  correctTimesByShipment,
  setTimeToIfNeeded,
} from 'state/shipments/effects';

function* correctTimes() {
  const shipmentKeys = yield select(orderSelectors.getShipmentKeys);
  yield all(
    shipmentKeys.map((shipmentKey) => call(correctTimesByShipment, shipmentKey))
  );
  return true;
}

function* handleTimePickers({ payload }) {
  const { stopType, timeParam, value, shipmentKey, diffDay } = payload;

  const key = yield call(utils.getTimeKey, stopType, timeParam);
  const fieldKey = yield call(utils.getTimeKeyFull, shipmentKey, key);
  const input = yield call(dateAndTimeFormatters.formatRawTime, value);

  const originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );
  let computedTimes = { ...originalTimes, [key]: input };
  const datekey = yield call(utils.getDateKey, stopType, timeParam);
  const dateFromkey = yield call(utils.getDateFromKey, stopType);

  const newDateWithDiff = yield call(
    dateAndTimeFormatters.getDateFormatted,
    computedTimes[dateFromkey],
    diffDay
  );

  computedTimes = { ...computedTimes, [datekey]: newDateWithDiff };

  let businessErrors = [];

  const autocompletedInput = yield call(
    dateAndTimeFormatters.autocompleteTimeInputIfCan,
    input
  );
  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    {
      ...computedTimes,
      [key]: autocompletedInput,
    },
    shipmentKey
  );

  if (!!errors && errors[key]) {
    businessErrors = errors[key];
  }

  let isNextDay = false;

  let computedTimesIfInputIsForNextDay = { ...computedTimes };

  if (
    !isEmpty(businessErrors) &&
    diffDay === 0 &&
    timeParam !== dateAndTimeConstants.TIME_FIELD_PARAMS.from
  ) {
    const newDateWithNextDiff = yield call(
      dateAndTimeFormatters.getDateFormatted,
      computedTimes[dateFromkey],
      diffDay + 1
    );
    computedTimesIfInputIsForNextDay = {
      ...computedTimes,
      [datekey]: newDateWithNextDiff,
    };

    const { errors: nextDayErrors } = yield call(
      dateAndTimeValidators.asyncValidateTimes,
      computedTimesIfInputIsForNextDay,
      shipmentKey
    );

    if (!nextDayErrors[key]) isNextDay = true;
  }

  if (!isNextDay) {
    yield put(
      errorActions.setError({
        [fieldKey]: businessErrors,
      })
    );
  }

  const newValues = isNextDay
    ? { ...computedTimesIfInputIsForNextDay }
    : { ...computedTimes };

  yield put(
    shipmentsActions.changeValues({ values: { ...newValues }, shipmentKey })
  );
}

function* setShipmentTimesWhenBlurred(payload) {
  const { shipmentKey, key, timeRaw: timeRawOriginal } = payload;

  const fieldKey = yield call(utils.getTimeKeyFull, shipmentKey, key);

  let timeRaw = timeRawOriginal;

  yield put(errorActions.removeError([fieldKey]));

  if (isEmpty(timeRaw)) {
    yield put(orderActions.fetchQuote());
    return;
  }

  let originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );
  if (timeRaw.length >= 3) {
    const autocompletedInput = yield call(
      dateAndTimeFormatters.autocompleteTimeInputIfCan,
      timeRaw
    );
    timeRaw = autocompletedInput;
    originalTimes = { ...originalTimes, [key]: autocompletedInput };
  }

  const { isValid } = yield call(dateAndTimeValidators.validateTime, timeRaw);

  let businessErrors = false;

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    originalTimes,
    shipmentKey
  );
  if (!!errors && errors[key]) {
    businessErrors = errors[key];
  }

  if ((!isValid && timeRaw) || businessErrors) {
    yield put(warningActions.setWarning({ [fieldKey]: 'warning' }));

    yield put(
      shipmentsActions.changeValues({
        values: {
          [key]: '',
        },
        shipmentKey,
      })
    );
  } else {
    const priorityRules = yield call(
      dateAndTimeValidators.priorityRules.time,
      key
    );

    const cleanedWithPriority = Object.keys(errors).reduce(
      (acc, errorKey) =>
        priorityRules &&
        priorityRules.indexOf(errorKey) >= 0 &&
        !!originalTimes[errorKey]
          ? {
              ...acc,
              [errorKey]: '',
            }
          : { ...acc },
      {}
    );

    const warnings = Object.keys(errors).reduce(
      (acc, errorKey) =>
        priorityRules &&
        priorityRules.indexOf(errorKey) >= 0 &&
        !!originalTimes[errorKey]
          ? {
              ...acc,
              [keyGenerator.dateTime({
                shipmentKey,
                dateTimeKey: errorKey,
              })]: 'warning',
            }
          : { ...acc },

      {}
    );

    yield all([
      put(warningActions.setWarning(warnings)),
      put(
        shipmentsActions.changeValues({
          values: { ...cleanedWithPriority, [key]: timeRaw },
          shipmentKey,
        })
      ),
    ]);

    yield call(setTimeToIfNeeded, { payload: { ...payload, time: timeRaw } });
  }
}

function* handleTimePickerBlur({ payload }) {
  const { stopType, timeParam, shipmentKey } = payload;
  const key = yield call(utils.getTimeKey, stopType, timeParam);
  const timeRaw = yield select(
    shipmentsSelectors.getShipmentTimeRaw,
    shipmentKey,
    null,
    key
  );

  if (stopType === dateAndTimeConstants.STOP_TYPES.pickup) {
    const shipmentKeys = yield select(orderSelectors.getShipmentKeys);
    yield all(
      shipmentKeys.map((k) =>
        call(setShipmentTimesWhenBlurred, {
          ...payload,
          key,
          shipmentKey: k,
          timeRaw,
        })
      )
    );
  } else {
    yield call(setShipmentTimesWhenBlurred, { ...payload, key, timeRaw });
  }

  yield put(orderActions.fetchQuote());
}

const shouldBeFixedWithPriorityRules = ({
  priorityRules,
  errorKey,
  computedTimes,
}) =>
  !!priorityRules &&
  priorityRules.indexOf(errorKey) >= 0 &&
  !!computedTimes[errorKey];

function* setShipmentTimesWhenSelected(payload) {
  const { shipmentKey, diffDay, key, input, datekey, dateFromkey } = payload;

  const originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );
  let computedTimes = { ...originalTimes, [key]: input };

  const newDateWithDiff = yield call(
    dateAndTimeFormatters.getDateFormatted,
    computedTimes[dateFromkey],
    diffDay
  );

  computedTimes = { ...computedTimes, [datekey]: newDateWithDiff };

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    computedTimes,
    shipmentKey
  );

  yield put(
    shipmentsActions.changeValues({ values: { ...computedTimes }, shipmentKey })
  );

  const priorityRules = yield call(
    dateAndTimeValidators.priorityRules.time,
    key
  );

  const computedValuesFixedWithPriority = Object.keys(errors).reduce(
    (acc, errorKey) =>
      shouldBeFixedWithPriorityRules({ priorityRules, errorKey, computedTimes })
        ? {
            ...acc,
            [errorKey]: '',
          }
        : { ...acc },
    {}
  );

  const warnings = Object.keys(errors).reduce(
    (acc, errorKey) =>
      shouldBeFixedWithPriorityRules({ priorityRules, errorKey, computedTimes })
        ? {
            ...acc,
            [keyGenerator.dateTime({
              shipmentKey,
              dateTimeKey: errorKey,
            })]: 'warning',
          }
        : { ...acc },

    {}
  );

  yield all([
    put(warningActions.setWarning(warnings)),
    put(
      shipmentsActions.changeValues({
        values: { ...computedValuesFixedWithPriority },
        shipmentKey,
      })
    ),
  ]);

  yield call(setTimeToIfNeeded, { payload: { ...payload, time: input } });
}

function* handleSelectTime({ payload }) {
  const { stopType, timeParam, value } = payload;

  const key = yield call(utils.getTimeKey, stopType, timeParam);
  const input = yield call(dateAndTimeFormatters.formatRawTime, value);

  const datekey = yield call(utils.getDateKey, stopType, timeParam);
  const dateFromkey = yield call(utils.getDateFromKey, stopType);

  if (stopType === dateAndTimeConstants.STOP_TYPES.pickup) {
    const shipmentKeys = yield select(orderSelectors.getShipmentKeys);
    yield all(
      shipmentKeys.map((shipmentKey) =>
        call(setShipmentTimesWhenSelected, {
          ...payload,
          key,
          input,
          datekey,
          dateFromkey,
          shipmentKey,
        })
      )
    );
  } else {
    yield call(setShipmentTimesWhenSelected, {
      ...payload,
      key,
      input,
      datekey,
      dateFromkey,
    });
  }
  yield put(orderActions.fetchQuote());
}

function* handleChangeDate(payload) {
  const { value, shipmentKey, key, dateKeys, timeKeysByStop, priorityRules } =
    payload;

  const times = yield select(shipmentsSelectors.getShipmentTimes, shipmentKey);
  const diffDays = moment(value).diff(moment(times[key]), 'days');

  const computedTimesWithNewDates = yield all(
    Object.keys(times).reduce(
      (acc, currKey) => ({
        ...acc,
        [currKey]:
          currKey === key
            ? dateAndTimeFormatters.getDateFormatted(value, 0)
            : dateKeys.indexOf(currKey) >= 0
            ? call(
                dateAndTimeFormatters.getDateFormatted,
                times[currKey],
                diffDays
              )
            : times[currKey],
      }),
      {}
    )
  );

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    computedTimesWithNewDates,
    shipmentKey
  );
  // const areErrors = Object.keys(errors).some( keyError => timeKeysByStop.indexOf(keyError) >= 0 )
  const areErrors = Object.keys(errors).reduce(
    (acc, keyError) => (timeKeysByStop.indexOf(keyError) >= 0 ? true : acc),
    false
  );

  const cleanedWithPriority = Object.keys(errors).reduce(
    (acc, errorKey) =>
      priorityRules && priorityRules.indexOf(errorKey) >= 0
        ? {
            ...acc,
            [keyGenerator.shipmentData.time({
              stopType: 'dropoff',
              timeParam: errorKey.split('_')[2],
            })]: '',
            [keyGenerator.shipmentData.date({
              stopType: 'dropoff',
              timeParam: errorKey.split('_')[2],
            })]: computedTimesWithNewDates.pickup_date_from,
          }
        : { ...acc },

    {}
  );

  if (areErrors) {
    const timesCleaned = timeKeysByStop.reduce(
      (acc, curKey) => ({ ...acc, [curKey]: '' }),
      {}
    );

    const newWarnings = timeKeysByStop.reduce(
      (acc, curKey) => ({
        ...acc,
        [utils.getTimeKeyFull(shipmentKey, curKey)]: 'warning',
      }),
      {}
    );

    yield all([
      put(
        shipmentsActions.changeValues({
          values: {
            ...computedTimesWithNewDates,
            ...cleanedWithPriority,
            ...timesCleaned,
          },
          shipmentKey,
        })
      ),
      put(warningActions.setWarning(newWarnings)),
    ]);
  } else {
    yield put(
      shipmentsActions.changeValues({
        values: { ...computedTimesWithNewDates, ...cleanedWithPriority },
        shipmentKey,
      })
    );
  }
}

function* handleDatePickers({ payload }) {
  const { stopType } = payload;

  const key = yield call(utils.getDateFromKey, stopType);
  const dateKeys = yield call(utils.getDateKeys, stopType);
  const timeKeysByStop = yield call(utils.getTimeKeysByStop, stopType);
  const priorityRules = yield call(
    dateAndTimeValidators.priorityRules.date,
    stopType
  );

  if (stopType === dateAndTimeConstants.STOP_TYPES.pickup) {
    const shipmentKeys = yield select(orderSelectors.getShipmentKeys);
    yield all(
      shipmentKeys.map((shipmentKey) =>
        call(handleChangeDate, {
          ...payload,
          shipmentKey,
          key,
          dateKeys,
          timeKeysByStop,
          priorityRules,
        })
      )
    );
  } else {
    yield call(handleChangeDate, {
      ...payload,
      key,
      dateKeys,
      timeKeysByStop,
      priorityRules,
    });
  }

  yield put(orderActions.fetchQuote());
}

function* handleSubmitAddressForm({ payload }) {
  const { stopType, address } = payload;

  const [key, rawKey, rawAddress] = yield all([
    call(utils.getAddressKey, stopType),
    call(utils.getAddressRawKey, stopType),
    call(addressFormatters.getAddressLabel, address),
  ]);

  if (stopType === dateAndTimeConstants.STOP_TYPES.pickup) {
    const shipmentKeys = yield select(orderSelectors.getShipmentKeys);
    yield all(
      shipmentKeys.map((shipmentKey) =>
        put(
          shipmentsActions.changeAddress({
            ...payload,
            shipmentKey,
            key,
            rawKey,
            rawAddress,
          })
        )
      )
    );
  } else {
    yield put(
      shipmentsActions.changeAddress({
        ...payload,
        key,
        rawKey,
        rawAddress,
      })
    );
  }

  yield put(orderActions.fetchQuote());
}

function* handleCleanAddress({ payload }) {
  const { stopType } = payload;

  const [key, rawKey] = yield all([
    call(utils.getAddressKey, stopType),
    call(utils.getAddressRawKey, stopType),
  ]);

  if (stopType === dateAndTimeConstants.STOP_TYPES.pickup) {
    const shipmentKeys = yield select(orderSelectors.getShipmentKeys);
    yield all(
      shipmentKeys.map((shipmentKey) =>
        put(
          shipmentsActions.changeValues({
            values: {
              [rawKey]: '',
              [key]: null,
            },
            shipmentKey,
          })
        )
      )
    );
  } else {
    yield put(
      shipmentsActions.changeValues({
        values: {
          [rawKey]: '',
          [key]: null,
        },
        ...payload,
      })
    );
  }

  yield put(orderActions.fetchQuote());
}

function* handleCreateShipment({ payload }) {
  const { shipmentKey } = payload;
  const shipments = yield select(orderSelectors.getShipmentKeys);
  const shipment = yield select(shipmentSelectors.getShipment, shipments[0]);

  /*
  address_to: null
  dropoff_comment_from_shipper_to_driver: ""
  dropoff_time_from: ""
  dropoff_time_to: ""
*/
  /*
  extras: [] ?
  internal_reference: ""
  //load_extra_info: ""
  //loads: ["FIRST"]
  //numLoads: 0
*/
  const {
    address_from,
    pickup_comment_from_shipper_to_driver,
    pickup_date_from,
    pickup_date_to,
    pickup_time_from,
    pickup_time_to,
    dropoff_date_from,
    dropoff_date_to,
  } = shipment;

  const overrideValues = {
    address_from,
    pickup_comment_from_shipper_to_driver,
    pickup_date_from,
    pickup_date_to,
    pickup_time_from,
    pickup_time_to,
    dropoff_date_from,
    dropoff_date_to,
  };

  yield all([
    put(
      shipmentsActions.addShipment({
        shipmentKey,
        values: { ...overrideValues },
      })
    ),
    put(
      actions.addShipment({
        shipmentKey,
      })
    ),
  ]);
}

export default function* () {
  yield takeLatest(types.createShipment$, handleCreateShipment);

  // timePickers
  yield takeLatest(types.changeTime$, handleTimePickers);
  yield takeLatest(types.selectTime$, handleSelectTime);
  yield takeLatest(types.onBlurTimePicker$, handleTimePickerBlur);

  // DatePickers
  yield takeLatest(types.changeDate$, handleDatePickers);

  // Addresses
  yield takeLatest(types.changeAddress$, handleSubmitAddressForm);
  yield takeLatest(types.cleanAddress$, handleCleanAddress);
}

export {
  correctTimes,
  handleTimePickers,
  handleTimePickerBlur,
  handleDatePickers,
};
